import {
  type MouseEventHandler,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTooltip } from '__components/Kizen/Tooltip';
import {
  StyledBadge,
  StyledDownArrow,
  StyledPill,
  StyledPillText,
} from '../styles';

type PillDropdownControlProps = {
  counts?: number;
  isError?: boolean;
  isOpen?: boolean;
  isReadOnly?: boolean;
  title: string;
  onClick(): void;
};

export const PillDropdownControl = forwardRef<
  HTMLDivElement,
  PillDropdownControlProps
>((props, ref) => {
  const { title, onClick, counts, isError, isOpen, isReadOnly } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [enabledTooltip, setEnabledTooltip] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const [{ onMouseEnter, onMouseLeave, onBlur, onFocus }, tooltip] = useTooltip(
    {
      label: title,
      delay: 500,
      popperConfig: {
        modifiers: {
          preventOverflow: { boundariesElement: 'viewport' },
          offset: { offset: '0px, -8px' },
        },
      },
    }
  );

  const handleMouseEnter: MouseEventHandler<HTMLDivElement> = (event) => {
    onMouseEnter(event);
    setIsHovered(true);
  };

  const handleMouseLeave: MouseEventHandler<HTMLDivElement> = (event) => {
    onMouseLeave(event);
    setIsHovered(false);
  };

  useEffect(() => {
    if (textRef.current) {
      const { scrollWidth, offsetWidth } = textRef.current;
      setEnabledTooltip(scrollWidth > offsetWidth);
    }
  }, []);

  return (
    <StyledPill
      ref={ref}
      onClick={onClick}
      isError={isError}
      isOpen={isOpen}
      isReadOnly={isReadOnly}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={onBlur}
      onFocus={onFocus}
    >
      {enabledTooltip ? tooltip : null}
      <StyledPillText ref={textRef}>{title}</StyledPillText>
      {counts ? <StyledBadge isError={isError}>{counts}</StyledBadge> : null}
      <StyledDownArrow
        icon="chevron-down"
        color={{
          light:
            isOpen || isHovered
              ? 'var(--color-font-primary)'
              : 'var(--color-font-placeholder)',
          dark: 'var(--color-font-primary)',
        }}
      />
    </StyledPill>
  );
});

PillDropdownControl.displayName = 'PillDropdownControl';
